import { useState, useEffect, useRef } from "react";
import { ElectricityEntry, Option } from "@/types/electricity";
import { normalizeText } from "@/utils/textFormatter";

interface UseElectricityFormArgs {
  formEntry: ElectricityEntry;
  sourceOptions: Option[];
  editing: boolean;
  onFormEntryChange: (entry: ElectricityEntry) => void;
  onSave: () => void;
}

export const useElectricityForm = ({
  formEntry,
  sourceOptions,
  editing,
  onFormEntryChange,
  onSave,
}: UseElectricityFormArgs) => {
  const dropdownRef = useRef<HTMLDivElement>(null);
  // Flag to track if user is typing
  const [userIsTyping, setUserIsTyping] = useState(false);

  const [search, setSearch] = useState(
    formEntry.energySource ? sourceOptions.find((opt) => opt.value === formEntry.energySource)?.label ?? "" : ""
  );
  const [showDropdown, setShowDropdown] = useState(false);
  const [touched, setTouched] = useState({
    energySource: false,
    quantity: false,
  });
  const [energySourceError, setEnergySourceError] = useState("");
  const [quantityError, setQuantityError] = useState(false);

  // Reset touched state when the modal is opened with a new form entry
  useEffect(() => {
    if (!userIsTyping) {
      setTouched({ energySource: false, quantity: false });
      setEnergySourceError("");
      setQuantityError(false);

      if (formEntry.energySource) {
        if (editing) {
          const matchingOption = sourceOptions.find((opt) => opt.value === formEntry.energySource);
          setSearch(matchingOption?.label || formEntry.energySource);
        } else {
          const matchingOption = sourceOptions.find((opt) => opt.value === formEntry.energySource);
          if (matchingOption) {
            setSearch(matchingOption.label);
          }
        }
      } else {
        setSearch("");
      }
    }
  }, [formEntry.energySource, sourceOptions, editing, userIsTyping]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Filter options based on search and editing state
  const filteredOptions = sourceOptions.filter(
    (option) => {
      const matchesSearch = normalizeText(option.label).includes(normalizeText(search)) || !search;
      const notAlreadySelected = !editing || option.value !== formEntry.energySource;
      return matchesSearch && notAlreadySelected;
    }
  ).sort((a, b) => a.label.localeCompare(b.label));

  /** Handle energy source search input change */
  const handleSourceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUserIsTyping(true);
    setSearch(e.target.value);
    setShowDropdown(true);

    if (!e.target.value) {
      onFormEntryChange({
        ...formEntry,
        energySource: "",
      });
    }
  };

  /** Handle energy source selection */
  const handleSourceSelect = (option: Option, e: React.MouseEvent) => {
    e.preventDefault();
    setShowDropdown(false);
    setSearch(option.label);

    setUserIsTyping(false);
    onFormEntryChange({
      ...formEntry,
      energySource: option.value,
    });
    setTouched({ ...touched, energySource: true });
    setEnergySourceError("");
  };

  /** Handle input click to show dropdown */
  const handleInputClick = () => {
    setShowDropdown(!showDropdown);
  };

  /** Handle energy source blur */
  const handleSourceBlur = () => {
    setTouched({ ...touched, energySource: true });

    setTimeout(() => {
      if (!dropdownRef.current?.contains(document.activeElement)) {
        setShowDropdown(false);
        setUserIsTyping(false);
      }
    }, 200);
  };

  /** Handle quantity change */
  const handleQuantityChange = (value: string) => {
    onFormEntryChange({
      ...formEntry,
      quantity: value === "" ? "" : parseFloat(value),
    });
    setTouched({ ...touched, quantity: true });
    setQuantityError(false);
  };

  /** Make sure quantity is a valid number on blur */
  const handleQuantityBlur = () => {
    setTouched({ ...touched, quantity: true });

    if (formEntry.quantity === "") {
      setQuantityError(true);
      return;
    }

    const value = Number(formEntry.quantity);
    if (isNaN(value) || value <= 0) {
      setQuantityError(true);
      return;
    }

    setQuantityError(false);
  };

  /** Validate form before save */
  const handleSaveClick = (t: (key: string, ...args: any[]) => string) => {
    // Mark all fields as touched when attempting to save
    setTouched({ energySource: true, quantity: true });

    let valid = true;

    // Energy source validation
    if (!formEntry.energySource) {
      setEnergySourceError(t("validation.required"));
      valid = false;
    }

    // Quantity validation
    if (formEntry.quantity === "" || formEntry.quantity === null || formEntry.quantity === undefined) {
      setQuantityError(true);
      valid = false;
    } else {
      const numValue = Number(formEntry.quantity);
      if (isNaN(numValue) || numValue <= 0) {
        setQuantityError(true);
        valid = false;
      }
    }

    if (valid) {
      onSave();
    }
  };

  // Check if form is valid for enabling/disabling save button
  const isFormValid = (): boolean => {
    if (!formEntry.energySource) return false;
    if (formEntry.quantity === "" || formEntry.quantity === null || formEntry.quantity === undefined) return false;
    const numValue = Number(formEntry.quantity);
    if (isNaN(numValue) || numValue <= 0) return false;
    return true;
  };

  return {
    dropdownRef,
    search,
    showDropdown,
    energySourceError,
    quantityError,
    touched,
    filteredOptions,
    isFormValid,
    handleSourceChange,
    handleSourceSelect,
    handleInputClick,
    handleSourceBlur,
    handleQuantityChange,
    handleQuantityBlur,
    handleSaveClick,
  };
};
